/**
 * useVideoRollCapture — motion-driven video roll capture (TICKET-290).
 *
 * Owns the RollVideoRecorder + MotionDetectionController + state machine + timers
 * so PracticePage and RollPage share a single behaviour for press-to-record,
 * motion-driven stop, no-motion timeout, and hard-cap fallback.
 *
 * State machine:
 *   idle ──start()──▶ waiting-for-motion ──motion──▶ rolling ──settled──▶ finalizing ──▶ done
 *                          │                            │
 *                          └─10s no motion──▶ timeout-no-motion (discard)
 *                                                       └─15s hard cap──▶ finalizing ──▶ done
 *
 * @module hooks/useVideoRollCapture
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { NO_MOTION_TIMEOUT_MS } from '@/constants/rollMedia';
import { captureFrame } from '@/services/camera';
import {
  MotionDetectionController,
  type MotionState
} from '@/services/motionDetection';
import {
  RollVideoRecorder,
  RollVideoRecorderError,
  type RollVideoRecorderErrorCode,
  type RollVideoRecording
} from '@/services/rollVideoRecorder';

const SERVICE_TAG = '[VIDEO_ROLL_CAPTURE]';

// Min time we must spend in 'rolling' before honoring `onSettled`. Guards against
// the press-shake → instant-settle false positive where the finger tap shakes the
// camera, motion detector emits 'moving', user holds steady, and settled fires
// 1.5s later from a non-roll. If too short, we reset the motion detector and wait
// for the next motion-then-settle cycle.
const MIN_ROLLING_DURATION_MS = 500;

/** Capture state reported to consumers. */
export type VideoCaptureState =
  | 'idle'
  | 'waiting-for-motion'
  | 'rolling'
  | 'finalizing'
  | 'done'
  | 'timeout-no-motion'
  | 'error';

/** Error code surfaced by the hook. */
export type VideoCaptureErrorCode = RollVideoRecorderErrorCode | 'STREAM_LOST';

/**
 * Hook return shape.
 */
export interface UseVideoRollCaptureResult {
  state: VideoCaptureState;
  /** Seconds remaining on no-motion countdown (only meaningful in waiting-for-motion). */
  secondsRemaining: number;
  /** Set when state === 'done'. */
  recording: RollVideoRecording | null;
  /** Set when state === 'error'. */
  errorCode: VideoCaptureErrorCode | null;
  errorMessage: string | null;
  /** Begin a capture. Stream + videoElement must be ready. No-op if state !== 'idle'. */
  start: (stream: MediaStream, videoElement: HTMLVideoElement) => void;
  /** Cancel and reset to 'idle'. Cleans up timers + recorder + motion detector. */
  abort: () => void;
  /** Reset to 'idle' after consuming a 'done' / 'error' / 'timeout-no-motion' state. */
  reset: () => void;
}

/**
 * Motion-driven video roll capture hook.
 *
 * @returns capture state, control callbacks, and result/error payloads.
 */
export function useVideoRollCapture(): UseVideoRollCaptureResult {
  const [state, setState] = useState<VideoCaptureState>('idle');
  const [secondsRemaining, setSecondsRemaining] = useState<number>(
    Math.ceil(NO_MOTION_TIMEOUT_MS / 1000)
  );
  const [recording, setRecording] = useState<RollVideoRecording | null>(null);
  const [errorCode, setErrorCode] = useState<VideoCaptureErrorCode | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const recorderRef = useRef<RollVideoRecorder | null>(null);
  const motionRef = useRef<MotionDetectionController | null>(null);
  const noMotionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Mirror of `state` for use inside async callbacks (avoids stale closures).
  const stateRef = useRef<VideoCaptureState>('idle');
  // Wall-clock ms when state entered 'rolling' (for MIN_ROLLING_DURATION_MS guard).
  const rollingStartedAtRef = useRef<number>(0);
  // Live still grabbed at settle (TICKET-293). Spread onto the recording payload
  // when stop() resolves so recognition gets a sharper input than re-decoding
  // the recorded blob would yield. Reset on start() and on abort/cleanup.
  const preFinalizedFrameRef = useRef<string | null>(null);

  const setStateBoth = useCallback((next: VideoCaptureState): void => {
    stateRef.current = next;
    setState(next);
  }, []);

  const clearTimers = useCallback((): void => {
    if (noMotionTimerRef.current) {
      clearTimeout(noMotionTimerRef.current);
      noMotionTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const cleanupCapture = useCallback((): void => {
    clearTimers();
    motionRef.current?.stop();
    motionRef.current = null;
  }, [clearTimers]);

  const reset = useCallback((): void => {
    cleanupCapture();
    recorderRef.current?.abort('cleanup');
    recorderRef.current = null;
    preFinalizedFrameRef.current = null;
    setRecording(null);
    setErrorCode(null);
    setErrorMessage(null);
    setSecondsRemaining(Math.ceil(NO_MOTION_TIMEOUT_MS / 1000));
    setStateBoth('idle');
  }, [cleanupCapture, setStateBoth]);

  const abort = useCallback((): void => {
    reset();
  }, [reset]);

  const start = useCallback((stream: MediaStream, videoElement: HTMLVideoElement): void => {
    if (stateRef.current !== 'idle') return;

    setRecording(null);
    setErrorCode(null);
    setErrorMessage(null);
    setSecondsRemaining(Math.ceil(NO_MOTION_TIMEOUT_MS / 1000));
    preFinalizedFrameRef.current = null;

    const recorder = new RollVideoRecorder();
    recorderRef.current = recorder;

    try {
      recorder.start(stream);
    } catch (err) {
      const code = err instanceof RollVideoRecorderError ? err.code : 'NO_MIME';
      setErrorCode(code);
      setErrorMessage(err instanceof Error ? err.message : 'Could not start recorder');
      setStateBoth('error');
      recorderRef.current = null;
      return;
    }

    setStateBoth('waiting-for-motion');

    // Awaiter for the recorder. Resolves when stop()/auto-stop completes; rejects on abort/error.
    recorder.stop().then((rec) => {
      const phase = stateRef.current;
      // Merge the live still grabbed at settle (TICKET-293) onto the recording.
      // Hard-cap path leaves preFinalizedFrameRef === null and recognition
      // falls through to extractFirstFrame.
      const merged: RollVideoRecording = preFinalizedFrameRef.current
        ? { ...rec, preFinalizedFrame: preFinalizedFrameRef.current }
        : rec;
      if (phase === 'rolling') {
        // Hard cap fired (or settle path already invoked stopNow). Finalize as done.
        cleanupCapture();
        setRecording(merged);
        setStateBoth('finalizing');
        // Single tick so consumers see 'finalizing' if they care; immediately move to 'done'.
        setStateBoth('done');
      } else if (phase === 'finalizing') {
        // Settle path already moved us to 'finalizing'; deliver the blob.
        setRecording(merged);
        setStateBoth('done');
      } else if (phase === 'waiting-for-motion') {
        // Hard cap fired with no motion ever detected — treat as timeout (discard).
        cleanupCapture();
        setStateBoth('timeout-no-motion');
      }
      // Other phases (idle / error / done / timeout-no-motion) mean we already cleaned up.
    }).catch((err: unknown) => {
      const phase = stateRef.current;
      if (phase === 'idle' || phase === 'timeout-no-motion') {
        // Expected: abort() / timeout discarded the recorder.
        return;
      }
      const code: VideoCaptureErrorCode = err instanceof RollVideoRecorderError
        ? err.code
        : 'NO_DATA';
      setErrorCode(code);
      setErrorMessage(err instanceof Error ? err.message : 'Recording failed');
      cleanupCapture();
      setStateBoth('error');
    });

    // Motion detector — drives transitions while recorder runs in the background.
    const motion = new MotionDetectionController();
    motionRef.current = motion;
    motion.start(videoElement, {
      onStateChange: (motionState: MotionState) => {
        if (motionState === 'moving' && stateRef.current === 'waiting-for-motion') {
          console.info(`${SERVICE_TAG} Motion detected, transition to rolling`);
          clearTimers();
          rollingStartedAtRef.current = Date.now();
          setStateBoth('rolling');
        }
      },
      onSettled: () => {
        if (stateRef.current !== 'rolling') return;
        const elapsed = Date.now() - rollingStartedAtRef.current;
        if (elapsed < MIN_ROLLING_DURATION_MS) {
          console.info(`${SERVICE_TAG} Premature settle (${elapsed}ms in rolling), resetting`);
          motionRef.current?.resetSettled();
          return;
        }
        console.info(`${SERVICE_TAG} Motion settled, finalizing`);
        // Grab a live still from the (still-running) video element BEFORE
        // stopping the recorder (TICKET-293). This skips the encoder quality
        // tax that re-decoding the recorded blob incurs. On failure (e.g.
        // stale element) we swallow and let recognition fall back to
        // extractFirstFrame on the recorded blob.
        try {
          preFinalizedFrameRef.current = captureFrame(videoElement, 0.9);
        } catch (err) {
          console.warn(`${SERVICE_TAG} preFinalize captureFrame failed`, err);
          preFinalizedFrameRef.current = null;
        }
        setStateBoth('finalizing');
        motionRef.current?.stop();
        motionRef.current = null;
        recorderRef.current?.stopNow();
      }
    });

    // 10s no-motion timeout — discards if no motion ever detected.
    noMotionTimerRef.current = setTimeout(() => {
      noMotionTimerRef.current = null;
      if (stateRef.current !== 'waiting-for-motion') return;
      console.info(`${SERVICE_TAG} No-motion timeout, discarding`);
      cleanupCapture();
      recorderRef.current?.abort('no-motion-timeout');
      recorderRef.current = null;
      setStateBoth('timeout-no-motion');
    }, NO_MOTION_TIMEOUT_MS);

    // 1Hz countdown for UI.
    countdownTimerRef.current = setInterval(() => {
      setSecondsRemaining((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
  }, [cleanupCapture, clearTimers, setStateBoth]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      clearTimers();
      motionRef.current?.stop();
      motionRef.current = null;
      recorderRef.current?.abort('unmount');
      recorderRef.current = null;
      preFinalizedFrameRef.current = null;
    };
  }, [clearTimers]);

  return {
    state,
    secondsRemaining,
    recording,
    errorCode,
    errorMessage,
    start,
    abort,
    reset
  };
}
